import {useEffect, useState} from 'react';

/**
 * Subscribes to a CSS media query and returns whether it currently matches,
 * updating as the viewport changes. SSR-safe: returns `false` when `window`
 * is unavailable.
 */
export function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(query).matches
    );

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const mediaQueryList = window.matchMedia(query);
        const handleChange = (event: MediaQueryListEvent) =>
            setMatches(event.matches);

        // Re-sync in case the match changed between render and effect (or the
        // query string changed).
        setMatches(mediaQueryList.matches);
        mediaQueryList.addEventListener('change', handleChange);
        return () => mediaQueryList.removeEventListener('change', handleChange);
    }, [query]);

    return matches;
}

/**
 * Bootstrap's `lg` breakpoint. Viewports narrower than 992px are treated as
 * "mobile" — the width at which the app switches to its mobile presentation.
 */
export const MOBILE_VIEWPORT_MAX_WIDTH = 991.98;

/**
 * Whether the viewport is below the `lg` (992px) breakpoint. Use this instead
 * of hand-wiring `window.innerWidth` checks with resize listeners.
 */
export function useIsMobileView(): boolean {
    return useMediaQuery(`(max-width: ${MOBILE_VIEWPORT_MAX_WIDTH}px)`);
}

/**
 * Bootstrap's `sm` breakpoint. Viewports narrower than 576px are the smallest
 * (phone) layout.
 */
export const SMALL_VIEWPORT_MAX_WIDTH = 575.98;

/**
 * Whether the viewport is below the `sm` (576px) breakpoint — the narrowest
 * phone layout.
 */
export function useIsSmallViewport(): boolean {
    return useMediaQuery(`(max-width: ${SMALL_VIEWPORT_MAX_WIDTH}px)`);
}
